/**
 * Judgement schemas (AGENT.md §6.4, §13, §21.7).
 *
 * A judge records a PASS / FAIL / OTHER verdict for each task (translation, reasoning).
 * On a FAIL the reviewer MAY tag the Kolectra linguistic taxonomy label (1–16) that
 * explains THAT task's failure (see `@kolectra/shared/taxonomy`) — the labels are per-task
 * (translation and reasoning each get their own, matching the sheet's `ANNOTATOR_LABEL_*`
 * columns) but are **optional**: a verdict with no label is still a valid, complete
 * judgement.
 *
 * `OTHER` is the "in between" verdict — the model was neither clearly right nor clearly
 * wrong. When either verdict is OTHER the upload route can't be derived from PASS/FAIL, so
 * the judge MUST pick it explicitly (`routeChoice`) and MUST leave a note (`comment`). When
 * both verdicts are PASS/FAIL the route stays deterministic (computed at approval) and both
 * `routeChoice` and `comment` are optional.
 */

import { z } from 'zod';
import { isValidFailureLabel } from '../taxonomy.js';

export const verdictSchema = z.enum(['PASS', 'FAIL', 'OTHER']);
export type Verdict = z.infer<typeof verdictSchema>;

/** The binary upload route — the judge's explicit choice when a verdict is OTHER. */
export const routeChoiceSchema = z.enum(['CHALLENGE_SET', 'NON_CHALLENGE_SET']);
export type RouteChoice = z.infer<typeof routeChoiceSchema>;

const failureLabelSchema = z
  .number()
  .int()
  .refine(isValidFailureLabel, { message: 'Unknown failure label.' });

const hasOther = (v: { translationVerdict: Verdict; reasoningVerdict: Verdict }): boolean =>
  v.translationVerdict === 'OTHER' || v.reasoningVerdict === 'OTHER';

export const judgementCreateSchema = z
  .object({
    translationVerdict: verdictSchema,
    reasoningVerdict: verdictSchema,
    comment: z.string().max(2000).optional(),
    /** Taxonomy id for the translation failure — optional, even on a FAIL. */
    translationFailureLabel: failureLabelSchema.optional(),
    /** Taxonomy id for the reasoning failure — optional, even on a FAIL. */
    reasoningFailureLabel: failureLabelSchema.optional(),
    /** Explicit route — REQUIRED when a verdict is OTHER, ignored/optional otherwise. */
    routeChoice: routeChoiceSchema.optional(),
  })
  .refine((v) => !hasOther(v) || v.routeChoice != null, {
    message: 'Pick Challenge set or Non-challenge set when a verdict is Other.',
    path: ['routeChoice'],
  })
  .refine((v) => !hasOther(v) || (v.comment != null && v.comment.trim().length > 0), {
    message: 'Add a note explaining the Other verdict.',
    path: ['comment'],
  });
export type JudgementCreateInput = z.infer<typeof judgementCreateSchema>;
